import React, { useCallback, useEffect, useState } from "react";
import { TableRow, TableCell, CircularProgress, Typography } from "@mui/material";
import ProjectCertifiedPayrollTableRow from "./ProjectCertifiedPayrollTableRow"; // Adjust the import path as necessary
import Loading from "../../../Components/Loading";
import { getApi, postApi } from "../../../../utils/services";
import { errorToast } from "../../../../utils/toastHelper";
import {
  GET_PROJECT_EMPLOYEE_DAILY_PAYROLL,
  GET_PROJECT_EMPLOYEE_DAILY_TIMESHEET,
  PROJECT_EMPLOYEE_DAILY_CERTIFIED_PAYROLL,
  GET_WORK_AREAS
} from "../../../../utils/services/apiPath";
import { formatCurrency } from "../../../Components/ProGrid/components/FormatCell";
import DownloadCell from "../../../Components/ProGrid/components/DownloadCell";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { createODataQuery } from "../../../../utils/services/oData";
import { GRID_FEATURE_MODE_CLIENT, GRID_FEATURE_MODE_SERVER, STAUTES } from "../../../../utils/constant";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

const ProjectCertifiedPayrollTable = ({
  id,
  loading,
  weeklyReportData,
  projectCertifiedPayrollPermission,
  setPageState,
  status,
  setStatus,
  setPagination,
  searchString,
  setSearchString,
  sortModel,
  setSortModel,
  rowCount,
  pageSize,
  currentPage,
  tokenData,
  dailyTimesheet,
  handleGenerateCPR,
  handlePayrollTimeSheet,
  handleGenerateCprWithType,
  selectedRow,
  setSelectedRow,
  companyName,
  setExportLoading
}) => {
  const [gridFeatureMode, setGridFeatureMode] = useState(GRID_FEATURE_MODE_SERVER);
  const [selectedVal, setSelectedVal] = useState({ id: null, bool: true });
  const [activeDailySheet, setActiveDailySheet] = useState({ name: "", date: "" });
  const [hasWorkAreas, setHasWorkAreas] = useState(false);
  const defaultOrderCol = selectedVal?.id === 1 ? "workedDate" : selectedVal?.id === 2 ? "createdDate" : "generatedDate";

  const orderByParams = React.useMemo(
    () => ({
      orderCol: sortModel?.orderCol || defaultOrderCol,
      orderByAscending: sortModel?.orderByAscending ?? false
    }),
    [sortModel?.orderCol, sortModel?.orderByAscending, defaultOrderCol]
  );

  useEffect(() => {
    if (!id) {
      setHasWorkAreas(false);
      return;
    }

    let isMounted = true;

    const checkWorkAreas = async () => {
        const response = await getApi(GET_WORK_AREAS(id));
        if (!isMounted) return;
        
        if (response?.error) {
          setHasWorkAreas(false);
          console.error("Failed to fetch project work areas", { projectId: id, error: response.error });
          return
        }
        
        const data = response?.data ?? response;
        setHasWorkAreas(Array.isArray(data) && data.length > 0);
    };
    checkWorkAreas();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleStatus = () => {
    if (status) return <Loading />;
    else return <Typography>No Data available</Typography>;
  };

  const rowData = dailyTimesheet?.map(item => ({
    ...item,
    id: uuidv4(),
    overTimePayRate: formatCurrency(item?.overTimePayRate, false),
    regularPayRate: formatCurrency(item?.regularPayRate, false),
    totalPay: formatCurrency(item?.totalPay, false),
    standardPayRate: formatCurrency(item?.standardPayRate, false),
    otPayRate: formatCurrency(item?.otPayRate, false),
    perDiem: formatCurrency(item?.perDiem, false),
    regularTotal: formatCurrency(item?.regularTotal, false),
    overTimeTotal: formatCurrency(item?.overTimeTotal, false),
    dtPayRate: formatCurrency(item?.dtPayRate, false),
    doubleTimePayRate: formatCurrency(item?.doubleTimePayRate, false),
    doubleTimeTotal: formatCurrency(item?.doubleTimeTotal, false)
  }));

  const handleDailyTimeSheet = useCallback(
    async row => {
        setActiveDailySheet({ name: "TimeSheet", date: row?.weekEndingDate });
        if (id) {
          setStatus(STAUTES.LOADING);
          const url = GET_PROJECT_EMPLOYEE_DAILY_TIMESHEET.replace("{projectGuid}", id).replace(
            "{weekEndingDate}",
            row?.weekEndingDate.replace(/\//g, "-")
          );
          const { data, totalRecords, error } = await getApi(
            `${
              url + createODataQuery([], [], `isValid eq ${selectedVal?.bool}`, searchString, orderByParams, pageSize, currentPage, true, "workedDate")
            }&clientGuid=${row.clientGuid}`
          );
          const formattedData = data?.map(item => ({ ...item, id: uuidv4() }));
          setStatus(STAUTES.IDLE);
          if (error) return errorToast(error);
          setPageState(prevPageInfo => ({ ...prevPageInfo, data: formattedData || [], rowCount: totalRecords }));
        }
    },
    [pageSize, currentPage, searchString, orderByParams, setPageState, setStatus, selectedVal]
  );

  const handleDailyPayroll = useCallback(
    async row => {
      setActiveDailySheet({ name: "Payroll Summary", date: row.weekEndingDate });
      if (id) {
        setStatus(STAUTES.LOADING);
        const payload = {
          pageIndex: searchString ? 1 : currentPage + 1,
          pageSize: pageSize,
          searchString: searchString,
          ...orderByParams
        };
        const { data, totalRecords, error } = await postApi(
          `${GET_PROJECT_EMPLOYEE_DAILY_PAYROLL}?projectId=${id}&weekEndingDate=${row?.weekEndingDate}&isValid=${selectedVal?.bool}&clientGuid=${row?.clientGuid}`,
          payload
        );
        const formattedData = (data || []).map(item => {
          const { checkNumber, ...rest } = item; 
          return {
            ...rest,
            id: uuidv4(),
            paycheckNumber: checkNumber || null 
          };
        });
        setStatus(STAUTES.IDLE);
        if (error) return errorToast(error);
        setPageState(prevPageInfo => ({ ...prevPageInfo, data: formattedData || [], rowCount: totalRecords }));
      }
    },
    [pageSize, currentPage, searchString, orderByParams, setPageState, setStatus, selectedVal]
  );

  const handleDailyCertifiedPayroll = useCallback(
    async row => {
      setActiveDailySheet({ name: "Certified Payroll", date: row.weekEndingDate });
      if (id) {
        setStatus(STAUTES.LOADING);
        const url = PROJECT_EMPLOYEE_DAILY_CERTIFIED_PAYROLL.replace("{projectGuid}", id);
        const oDataQuery = createODataQuery(
          [], [], `weekEndingDate eq '${row?.weekEndingDate}'`, searchString, orderByParams, pageSize, currentPage
        );
        const { data, totalRecords, error } = await getApi(
          `${url}${oDataQuery}&clientGuid=${row?.clientGuid}`
        );
        const formattedData = data?.map(item => ({ ...item, id: uuidv4() }));
        setStatus(STAUTES.IDLE);
        if (error) return errorToast(error);
        setPageState(prevPageInfo => ({ ...prevPageInfo, data: formattedData || [], rowCount: totalRecords }));
      }
    },
    [pageSize, currentPage, searchString, orderByParams, setPageState, setStatus]
  );

  const handleDownload = params => {
    const file = params.row.path;
    const link = document.createElement("a");
    link.href = file;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const getAllTimeSheetData = async () => {
    const url = GET_PROJECT_EMPLOYEE_DAILY_TIMESHEET.replace("{projectGuid}", id).replace(
      "{weekEndingDate}",
      selectedRow?.weekEndingDate.replace(/\//g, "-")
    );
    const { data, error } = await getApi(
      `${url + createODataQuery([], [], `isValid eq ${selectedVal?.bool}`, "", orderByParams, 10000, 0, true, "workedDate")}`
    );
    if (error) {
      errorToast(error);
      return [];
    }
    return data?.map(item => ({ ...item, id: uuidv4() }));
  };

  const getAllPayrollData = async () => {
    const payload = {
      pageIndex: 1,
      pageSize: 10000,
      searchString: "",
      ...orderByParams
    };
    const { data, error } = await postApi(
      `${GET_PROJECT_EMPLOYEE_DAILY_PAYROLL}?projectId=${id}&weekEndingDate=${selectedRow?.weekEndingDate}&isValid=${selectedVal?.bool}&clientGuid=${selectedRow?.clientGuid}`,
      payload
    );
    if (error) {
      errorToast(error);
      return [];
    }
    return data?.map(item => {
      const { checkNumber, ...rest } = item;
      return { ...rest, id: uuidv4(), paycheckNumber: checkNumber || null };
    });
  };

  const customExportAction = async (apiRef, type) => {
    const currentData = _.cloneDeep(rowData);
    setGridFeatureMode(GRID_FEATURE_MODE_CLIENT);
    setPageState(prevPageInfo => ({ ...prevPageInfo, pageSize: 10000 }));
    setExportLoading(true);
    let data = [];
    if (selectedVal?.id === 1) {
      data = await getAllTimeSheetData();
    } else if (selectedVal?.id === 2) {
      data = await getAllPayrollData();
    } else if (selectedVal?.id === 3) {
      data = _.cloneDeep(rowData);
    }
    if (data.length === 0) {
      setGridFeatureMode(GRID_FEATURE_MODE_SERVER);
      setPageState(prevPageInfo => ({ ...prevPageInfo, pageSize: 10 }));
      setExportLoading(false);
      return;
    }
    await apiRef.setRows(data);
    setTimeout(async () => {
      if (type === "csv") {
        await apiRef?.exportDataAsCsv();
      } else if (type === "print") {
        await apiRef?.exportDataAsPrint();
      }
      setExportLoading(false);
      setGridFeatureMode(GRID_FEATURE_MODE_SERVER);
      setPageState(prevPageInfo => ({ ...prevPageInfo, pageSize: 10, data: currentData, currentPage }));
    }, 2000);
  };

  useEffect(() => {
    if (!selectedRow) {
      setSelectedVal({ id: null, bool: true });
    }
  }, [selectedRow]);

  useEffect(() => {
      if (selectedRow && selectedVal.id) {
        if (selectedVal?.id === 1) {
          handleDailyTimeSheet(selectedRow);
        } else if (selectedVal?.id === 2) {
          handleDailyPayroll(selectedRow);
        } else {
          handleDailyCertifiedPayroll(selectedRow);
        }
      }
    
  }, [currentPage, sortModel?.orderCol, sortModel?.orderByAscending, selectedRow, selectedVal?.id, selectedVal?.bool]);

  const formatDate = timestamp => {
    const date = new Date(timestamp);
    dayjs.extend(localizedFormat);
    return dayjs(date).format("L LT");
  };

  const columns = React.useMemo(() => {
    if (!dailyTimesheet?.[0]) return [];

    const baseColumns = Object.keys(dailyTimesheet[0])
      .filter(
        key =>
          ![
            "id",
            "payrollTimeSheetId",
            "partnerName",
            "payrollNumber",
            "employeeId",
            "isManual",
            "isSigned",
            "isValid",
            ...(!hasWorkAreas ? ["workAreaName"] : [])
          ].includes(key)
      )
      .map(key => {
        let headerName = key.charAt(0).toUpperCase() + key.slice(1);

        if (key === "internalEmployeeId") {
          headerName = "Internal Employee ID";
        }
        if (key === "workAreaName") {
          headerName = "Work Area";
        }
        if (key === "totalOtHours") {
          headerName = "Total OT Hours";
        }
        if (key === "totalDtHours") {
          headerName = "Total DT Hours";
        }

        if (key === "status") {
          headerName = selectedVal?.bool === false ? "Failure Reason" : "Status";
        }

        let modifiedHeaderName = headerName.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
        let columnDefinition = { field: key, headerName: modifiedHeaderName };
        if (key === "path") {
          columnDefinition.renderCell = params => <DownloadCell params={params} onOpen={() => handleDownload(params)} />;
        }

        if (["generatedDate"].includes(key)) {
          columnDefinition.renderCell = params => <Typography variant="body2">{formatDate(params?.row?.generatedDate)}</Typography>;
        }
        return columnDefinition;
      });

    const statusColumn = baseColumns.find(column => column.field === "status");
    const columnsAfterStatusMove = selectedVal?.bool === false && statusColumn
      ? [statusColumn, ...baseColumns.filter(column => column.field !== "status")]
      : baseColumns;

    const pathColumn = columnsAfterStatusMove.find(column => column.field === "path");
    if (!pathColumn) return columnsAfterStatusMove;

    return [...columnsAfterStatusMove.filter(column => column.field !== "path"), pathColumn];
  }, [dailyTimesheet, hasWorkAreas, selectedVal?.bool]);

  function renderContent() {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={12} align="center">
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    } else if (weeklyReportData?.length === 0) {
      return (
        <TableRow>
          <TableCell className="no-data" colSpan={12} align="center">
            No Rows
          </TableCell>
        </TableRow>
      );
    } else {
      return weeklyReportData?.map(row => (
        <ProjectCertifiedPayrollTableRow
          key={row.id}
          row={{ ...row, showGenerate: row.clientName === companyName }}
          permission={projectCertifiedPayrollPermission?.fieldPerObj?.TimeSheet?.canView}
          rowData={rowData}
          selectedVal={selectedVal}
          setSelectedVal={setSelectedVal}
          status={status === STAUTES.LOADING}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          handleStatus={handleStatus}
          setPagination={setPagination}
          setSearchString={setSearchString}
          setSortModel={setSortModel}
          rowCount={rowCount}
          activeDailySheet={activeDailySheet}
          columns={columns}
          searchString={searchString}
          pageSize={pageSize}
          currentPage={currentPage}
          sortModel={sortModel}
          tokenData={tokenData}
          handleGenerateCPR={handleGenerateCPR}
          handlePayrollTimeSheet={handlePayrollTimeSheet}
          handleGenerateCprWithType={handleGenerateCprWithType}
          customExportAction={customExportAction}
          gridFeatureMode={gridFeatureMode}
        />
      ));
    }
  }

  return <>{renderContent()}</>;
};

ProjectCertifiedPayrollTable.propTypes = {
  id: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  weeklyReportData: PropTypes.array,
  projectCertifiedPayrollPermission: PropTypes.object,
  STAUTES: PropTypes.object.isRequired,
  setPageState: PropTypes.func.isRequired,
  status: PropTypes.bool.isRequired,
  setStatus: PropTypes.func.isRequired,
  setPagination: PropTypes.func.isRequired,
  searchString: PropTypes.string,
  setSearchString: PropTypes.func.isRequired,
  sortModel: PropTypes.object,
  setSortModel: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  tokenData: PropTypes.object.isRequired,
  dailyTimesheet: PropTypes.array,
  handleGenerateCprWithType: PropTypes.func.isRequired
};

export default ProjectCertifiedPayrollTable;
